'use client';

/**
 * CodeEditor — the design system's Monaco surface.
 *
 * Holds no static Monaco import: the API, the requested features and the
 * requested languages are all dynamic imports resolved on mount. Statically
 * importing this component is therefore cheap, and consumers do not need to wrap
 * it in React.lazy to avoid paying for Monaco on pages that never render it.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { tokens } from '@/tokens';
import { loadMonaco } from './loader';
import { MONACO_THEME_ID } from './theme';
import { LANGUAGES } from './registry';
import type { BuiltinLanguageId, FeatureId } from './registry';
import type { LanguageInput, MonacoApi } from './types';

type MonacoEditorInstance = ReturnType<MonacoApi['editor']['create']>;

/**
 * How many un-acknowledged outputs to remember per editor. A consumer that has
 * fallen more than this many keystrokes behind is not a lag any more, and the
 * cap keeps a consumer that never echoes at all (a `value` pinned to a
 * constant) from growing the set without bound.
 */
const EMITTED_HISTORY_LIMIT = 64;

function rememberEmitted(history: Set<string>, value: string): void {
  history.add(value);
  if (history.size > EMITTED_HISTORY_LIMIT) {
    // Sets iterate in insertion order, so this drops the oldest.
    history.delete(history.values().next().value!);
  }
}

/** Collapses the history to a single value once nothing is in flight. */
function settleEmitted(history: Set<string>, value: string): void {
  history.clear();
  history.add(value);
}

/**
 * `navigator.platform` is deprecated and `userAgentData` is not universal, so
 * check both and degrade to the Ctrl label rather than guessing.
 */
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const data = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  const platform = data?.platform ?? navigator.platform ?? '';
  return /mac|iphone|ipad/i.test(platform);
}

/**
 * The keystroke that toggles Monaco's tab-focus mode, per platform.
 *
 * Read these off Monaco's own registration rather than assuming the usual
 * Ctrl/Cmd swap — `ToggleTabFocusModeAction` binds `CtrlCmd|KeyM` generally but
 * `WinCtrl|Shift|KeyM` on macOS, i.e. **Ctrl+Shift+M**, not Cmd+M. Printing
 * `⌘M` there is worse than printing nothing: macOS takes Cmd+M for
 * minimise-window, so anyone following the hint has their window disappear and
 * never reaches the escape it was advertising.
 */
function tabFocusShortcutLabel(): string {
  return isApplePlatform() ? '⌃⇧M' : 'Ctrl+M';
}

interface CodeEditorBaseProps {
  value: string;
  onChange?: (value: string) => void;

  /** Replaces DEFAULT_FEATURES entirely when provided. */
  features?: FeatureId[];

  readOnly?: boolean;
  height?: number | string;

  /**
   * Shows a hint in the corner, while the editor has focus, naming the
   * keystroke that makes Tab move focus instead of inserting a tab character.
   *
   * **Off by default**, and deliberately so. Tab inserting a tab character is a
   * WCAG 2.1.2 keyboard trap and the escape is undiscoverable, which argues for
   * advertising it — but the hint is permanent chrome inside the editor for
   * every user, the overwhelming majority of whom navigate with a pointer,
   * never press Tab expecting to leave, and read it as noise. Whether that
   * trade is worth making depends on the surface: a settings form a keyboard
   * user tabs through end to end is a different case from a modal whose editor
   * is reached by clicking it. That is the consumer's call, not the design
   * system's, so it is opt-in.
   *
   * Switching this on changes nothing about behaviour — Tab still inserts a tab
   * character, and the escape keystroke works either way, because
   * `toggleTabFocusMode` is in `DEFAULT_FEATURES`. The only thing this prop
   * controls is whether the keystroke is advertised on screen.
   */
  showTabFocusHint?: boolean;

  /**
   * Required. Monaco renders a bare textarea to assistive technology, so an
   * unlabelled editor is unusable with a screen reader.
   */
  ariaLabel: string;

  /**
   * Merged over the design system's defaults. Spread LAST — both at create
   * time and on every options-sync effect — so a consumer who puts
   * `readOnly` or `ariaLabel` inside `options` silently defeats the
   * top-level prop of the same name. The same applies to `editContext`, which
   * is pinned false on purpose — see the create call below before overriding it.
   */
  options?: Parameters<MonacoApi['editor']['create']>[1];
  /** Runs after create. May return a cleanup function. */
  onMount?: (editor: MonacoEditorInstance, monaco: MonacoApi) => void | (() => void);

  loadingFallback?: ReactNode;
  onLoadError?: (error: Error) => void;
}

/**
 * At least one of `language` or `languages` must be named — otherwise there is
 * nothing to tell the editor what to mount as. Modelled as a union of two
 * "one side required" shapes, rather than both fields being independently
 * optional, so `<CodeEditor value={v} ariaLabel={a} />` (neither supplied) is
 * a compile error instead of a runtime surprise (silently mounting as plain
 * text). `language` alone, `languages` alone, and both together all satisfy
 * one arm or the other.
 */
export type CodeEditorLanguageProps =
  | {
      /** Active language id. Defaults to the sole entry when `languages` has one. */
      language: string;
      /** Languages to make available. Defaults to `[language]` when built-in. */
      languages?: LanguageInput<BuiltinLanguageId>[];
    }
  | {
      /** Active language id. Defaults to the sole entry when `languages` has one. */
      language?: string;
      /** Languages to make available. The sole entry becomes the active language. */
      languages: LanguageInput<BuiltinLanguageId>[];
    };

export type CodeEditorProps = CodeEditorBaseProps & CodeEditorLanguageProps;

export function CodeEditor({
  value,
  onChange,
  language,
  languages,
  features,
  readOnly = false,
  height = '100%',
  showTabFocusHint = false,
  ariaLabel,
  options,
  onMount,
  loadingFallback = null,
  onLoadError,
}: Readonly<CodeEditorProps>) {
  const muiTheme = useTheme();
  const mode = muiTheme.palette.mode === 'light' ? 'light' : 'dark';

  const host = useRef<HTMLDivElement | null>(null);
  const editor = useRef<MonacoEditorInstance | null>(null);
  const monacoRef = useRef<MonacoApi | null>(null);
  const [ready, setReady] = useState(false);
  const [focused, setFocused] = useState(false);

  // Held in refs so the mount effect never lists them as dependencies. Refs
  // are updated in an effect rather than inline during render — assigning
  // `.current` in the render body itself is flagged by this repo's
  // react-hooks/refs lint rule, and since these callbacks only ever fire
  // asynchronously (after Monaco has mounted), updating them a tick later in
  // an effect is behaviorally equivalent.
  const onChangeRef = useRef(onChange);
  const onMountRef = useRef(onMount);
  const onLoadErrorRef = useRef(onLoadError);
  useEffect(() => {
    onChangeRef.current = onChange;
    onMountRef.current = onMount;
    onLoadErrorRef.current = onLoadError;
  }, [onChange, onMount, onLoadError]);

  /**
   * The last text this editor emitted upward. When `value` comes back equal to
   * it, the change originated here and must not be written back — see the sync
   * effect below.
   */
  const emitted = useRef(value);

  /**
   * Every value this editor has emitted that the consumer has not acknowledged
   * yet, plus the one it mounted with.
   *
   * `emitted` alone only recognises an echo that comes straight back. Consumers
   * render behind the typist — `onChange` drives a `setState`, React coalesces
   * queued updates and commits whichever landed last — so the value that
   * arrives can be an EARLIER output of this editor rather than the latest one.
   * Matched against `emitted` alone that looks exactly like an external edit,
   * and writing it back costs the characters typed since AND drops the caret at
   * position 0, so everything typed next lands at the top of the document.
   *
   * Anything in here came from this editor, so it is never news, and the
   * consumer is always free to catch up. Once it does (`value === emitted`)
   * nothing is in flight and the history collapses to that one value, which
   * keeps a later external edit that happens to equal something typed earlier
   * in the session applying normally.
   */
  const emittedHistory = useRef(new Set([value]));

  // Snapshot the initial value and language: the mount effect runs once, and
  // later changes are pushed in by the effects further down.
  const initial = useRef({ value, language, languages, features });

  useEffect(() => {
    const node = host.current;
    if (!node) return undefined;

    // The mount path awaits several times. Under StrictMode's double-invoke the
    // cleanup runs mid-flight, so every await is followed by a disposed check —
    // without it, the first pass finishes and leaks a second editor.
    let disposed = false;
    let cleanupFromMount: (() => void) | undefined;

    const resolvedLanguages: LanguageInput<BuiltinLanguageId>[] =
      initial.current.languages ??
      (initial.current.language ? [initial.current.language as BuiltinLanguageId] : []);

    const activeLanguage =
      initial.current.language ??
      (typeof resolvedLanguages[0] === 'string' ? resolvedLanguages[0] : resolvedLanguages[0]?.id);

    void (async () => {
      try {
        const monaco = await loadMonaco({
          languages: resolvedLanguages,
          features: initial.current.features,
          mode,
        });
        if (disposed) return;

        // Monaco measures character width at create time. A late-arriving font
        // would leave the cursor misaligned from the glyphs. The current mono
        // token is a native stack, so this is defensive — and free.
        if (typeof document !== 'undefined' && document.fonts?.ready) {
          await document.fonts.ready;
          if (disposed) return;
        }

        monacoRef.current = monaco;
        const instance = monaco.editor.create(node, {
          value: initial.current.value,
          language: activeLanguage,
          automaticLayout: true,
          fontFamily: tokens.typography.fontFamily.mono,
          fontSize: 13,
          lineHeight: 22,
          minimap: { enabled: false },
          scrollBeyondLastLine: false,
          renderLineHighlight: 'line',
          padding: { top: 12, bottom: 12 },
          readOnly,
          ariaLabel,
          accessibilitySupport: 'auto',
          fixedOverflowWidgets: true,
          // Keep the typing surface a real form control.
          //
          // Monaco 0.56 turns this on wherever the browser ships the EditContext
          // API, which replaces the hidden `<textarea class="inputarea">` with a
          // bare `<div class="native-edit-context">`. Inside Monaco that is an
          // improvement; from outside it is invisible. Host applications route
          // their global hotkeys through one heuristic — is the event target an
          // INPUT / SELECT / TEXTAREA, or contenteditable? — and that div answers
          // no to all four, so a document-level shortcut happily cancels keys the
          // user is typing in here. React Flow's space-to-pan (on by default)
          // eating every space typed into an editor rendered beside a canvas is
          // the case that found this; command palettes and hotkey libraries make
          // the same check.
          //
          // A design system cannot fix that at each consumer, and it is not
          // reasonable to ask every host to special-case Monaco's private DOM. So
          // the editor stays on the textarea path, which those handlers already
          // recognise. `TypingSurfaceIsAnInput` in the stories holds the line.
          //
          // The cost is EditContext's better IME composition handling. A consumer
          // who wants it back can pass `options={{ editContext: true }}` — the
          // spread below is last — and take on the key-conflict problem knowingly.
          editContext: false,
          ...options,
        });

        if (disposed) {
          instance.getModel()?.dispose();
          instance.dispose();
          return;
        }

        editor.current = instance;

        const subscriptions = [
          instance.onDidChangeModelContent(() => {
            const next = instance.getValue();
            emitted.current = next;
            rememberEmitted(emittedHistory.current, next);
            onChangeRef.current?.(next);
          }),
          // Drives the tab-focus hint below.
          instance.onDidFocusEditorText(() => setFocused(true)),
          instance.onDidBlurEditorText(() => setFocused(false)),
        ];

        cleanupFromMount = () => {
          subscriptions.forEach((subscription) => subscription.dispose());
          instance.getModel()?.dispose();
          instance.dispose();
          editor.current = null;
        };

        const fromConsumer = onMountRef.current?.(instance, monaco);
        if (fromConsumer) {
          const disposeEditor = cleanupFromMount;
          cleanupFromMount = () => {
            fromConsumer();
            disposeEditor();
          };
        }

        setReady(true);
      } catch (error) {
        if (disposed) return;
        onLoadErrorRef.current?.(error instanceof Error ? error : new Error(String(error)));
      }
    })();

    return () => {
      disposed = true;
      cleanupFromMount?.();
    };
    // Mount once. Prop changes are applied by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Adopt an externally-driven value.
   *
   * Writing back on any difference corrupts fast typing: keystrokes outrun
   * React, so this effect can fire holding a stale `value`, and `setValue` then
   * rewrites the document and drops the caret at the start. Comparing against
   * what this editor last emitted distinguishes a genuine external change from
   * our own edit echoing back through React.
   */
  useEffect(() => {
    const instance = editor.current;
    if (!instance) return;
    if (value === emitted.current) {
      // The consumer is level with this editor: nothing older is still in
      // flight, so no earlier output needs remembering.
      settleEmitted(emittedHistory.current, value);
      return;
    }
    if (instance.getValue() === value) return;
    // An earlier output of this editor, arriving late. Not news, and writing it
    // back would undo the characters typed since and reset the caret.
    if (emittedHistory.current.has(value)) return;
    emitted.current = value;
    settleEmitted(emittedHistory.current, value);
    instance.setValue(value);
    // `ready` is a dependency, not just a read: the mount effect awaits Monaco
    // and `document.fonts.ready` before `editor.current` exists, so if `value`
    // changes while that's in flight this effect bails above with no editor to
    // write to — and with `ready` absent from the deps, it would never be given
    // another chance to run once the editor exists. Re-running when `ready`
    // flips reconciles the freshly-created editor (which holds `initial.current
    // .value`, the value snapshotted at mount) against whatever `value` is by
    // then, so a prop change during the load window is not silently dropped.
  }, [value, ready]);

  /**
   * Adopt a changed active language.
   *
   * `language` names which grammar the document is currently in, and consumers
   * that offer a language picker change it on a live editor. The mount effect
   * snapshots it, so without this the model keeps whatever it was created with:
   * the picker moves, the highlighting and completions do not.
   *
   * Loading before switching is the part that is easy to miss. The mount
   * registers only the languages named at the time, so switching to a built-in
   * that was not among them would set an id Monaco has no tokenizer for — the
   * model would report the new language while the text rendered as
   * undifferentiated plain text. `loadMonaco` is idempotent per id (it tracks
   * what it has registered), so re-requesting an already-loaded language costs
   * nothing. Ids that are not built-ins came from the consumer's own
   * `languages` entries and were registered at mount, so they switch directly.
   *
   * This deliberately does NOT extend to `languages` or `features` changing —
   * see the options effect below for why those still require a remount.
   */
  useEffect(() => {
    const instance = editor.current;
    const monaco = monacoRef.current;
    if (!instance || !monaco || !language) return undefined;
    if (instance.getModel()?.getLanguageId() === language) return undefined;

    // The load is awaited, so a second switch (or an unmount) can land first.
    let cancelled = false;
    void (async () => {
      if (language in LANGUAGES) {
        await loadMonaco({
          languages: [language as BuiltinLanguageId],
          features: initial.current.features,
          mode,
        });
        if (cancelled) return;
      }
      // Re-read rather than closing over the model: the editor may have been
      // disposed, or the language may have moved again, while the load ran.
      const model = editor.current?.getModel();
      if (!model || model.getLanguageId() === language) return;
      monaco.editor.setModelLanguage(model, language);
    })();

    return () => {
      cancelled = true;
    };
    // `mode` is a dependency only because `loadMonaco` takes it; a theme change
    // re-runs this and bails immediately on the language check above.
  }, [language, mode, ready]);

  /**
   * The last `{ readOnly, ariaLabel, options }` combination actually applied
   * to a live editor, serialized so content — not identity — is what's
   * compared below.
   */
  const appliedOptions = useRef<
    { readOnly: boolean; ariaLabel: string; optionsJson: string | undefined } | undefined
  >(undefined);

  // The mount effect snapshots props, so anything that can change afterwards
  // must be pushed in explicitly. `languages` and `features` are deliberately
  // NOT here: re-registering a language id throws, and loading a feature after
  // create does not retroactively attach it to a live editor. Changing either
  // requires remounting the component. (The active `language` is a different
  // matter and is adopted by the effect above — switching which registered
  // grammar a model uses is supported; changing the registered set is not.)
  useEffect(() => {
    const instance = editor.current;
    if (!instance) return;

    // `options={{ ... }}` written inline at the call site is an entirely
    // natural pattern, and it gets a new object identity every render — in
    // the primary controlled use case, every keystroke, since `onChange`
    // drives a parent `setState`. Comparing serialized content rather than
    // identity means `updateOptions` (and whatever relayout/rerender it
    // costs inside Monaco) only actually runs when something meaningfully
    // changed. Monaco's option values are plain JSON-serializable data, so
    // `JSON.stringify` is an adequate content key here.
    const optionsJson = options ? JSON.stringify(options) : undefined;
    const previous = appliedOptions.current;
    const unchanged =
      previous !== undefined &&
      previous.readOnly === readOnly &&
      previous.ariaLabel === ariaLabel &&
      previous.optionsJson === optionsJson;
    if (unchanged) return;

    instance.updateOptions({ readOnly, ariaLabel, ...options });
    appliedOptions.current = { readOnly, ariaLabel, optionsJson };
    // `ready` is a dependency for the same reason as the value-sync effect
    // above: this bails above while the editor is still loading, and without
    // `ready` in the deps it would never get another chance to apply
    // `readOnly`/`ariaLabel`/`options` once the editor actually exists.
  }, [readOnly, ariaLabel, options, ready]);

  useEffect(() => {
    // Monaco's theme is page-global; this follows the MUI provider.
    //
    // `ready` is a dependency for the same reason as the two effects above,
    // and this is the third instance of the same shape: `monacoRef.current` is
    // null for the whole async mount window, so a `mode` change during it is
    // swallowed by the `?.` and — without `ready` in the deps — never gets a
    // second chance. The symptom is milder than the value/readOnly case (the
    // editor keeps the mode it was created with until the next toggle) but the
    // defect is identical, so the fix is too.
    const monaco = monacoRef.current;
    if (!monaco) return;
    monaco.editor.setTheme(MONACO_THEME_ID[mode]);

    // Then force a re-tokenization.
    //
    // Monaco caches colour-map INDICES in a model's tokens, not colours, and
    // does not re-tokenize when the theme changes. The map is also
    // de-duplicated, so two themes only agree on what slot N means if their
    // palettes collapse identically — GitHub's do not (in light, `regexp`
    // shares a colour with `string`; in dark it does not). Lines tokenized
    // before the switch therefore repaint with unrelated colours, while the
    // chrome updates correctly because it is plain CSS: the editor ends up
    // looking like a different theme entirely.
    //
    // Bouncing the model through `plaintext` and back is the cheapest public
    // API that forces a genuine re-tokenization. `setModelLanguage` no-ops when
    // the id is unchanged, so re-setting the current language would not work.
    // Content, selection and undo history are all preserved.
    const model = editor.current?.getModel();
    const language = model?.getLanguageId();
    if (model && language && language !== 'plaintext') {
      monaco.editor.setModelLanguage(model, 'plaintext');
      monaco.editor.setModelLanguage(model, language);
    }
  }, [mode, ready]);

  return (
    <Box sx={{ position: 'relative', height, width: '100%' }}>
      <Box
        ref={host}
        data-testid="code-editor-host"
        sx={{ height: '100%', width: '100%' }}
      />
      {!ready && loadingFallback}
      {/*
        Tab inserts a tab character rather than moving focus, which is a
        keyboard trap under WCAG 2.1.2. Monaco's escape is undiscoverable, so a
        consumer can opt into stating it — see `showTabFocusHint`. Shown only
        while the editor has focus, so it never sits on an idle editor.
      */}
      {showTabFocusHint && focused && (
        <Box
          data-testid="code-editor-tab-hint"
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 4,
            px: 0.75,
            fontSize: 11,
            lineHeight: 1.6,
            pointerEvents: 'none',
            borderRadius: 0.5,
            opacity: 0.9,
            color: 'text.secondary',
            backgroundColor: 'background.paper',
          }}
        >
          Press {tabFocusShortcutLabel()} to exit with Tab
        </Box>
      )}
    </Box>
  );
}

export default CodeEditor;
